/**
 * ReferenceContextSection — iter981 · Sprint 2
 *
 * Read-only-ish UI for the 16 NON-modifiable BHI risk factors ("Референтен
 * контекст"). Values are pre-filled from the user profile where possible
 * (height, family history) and marked as auto-populated. The user can
 * override any of them; overrides persist in `bhi_reference_context`.
 *
 * Placed BELOW the modifiable PreventionPathSection in the BHI page.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, Save, RotateCcw, ChevronDown, ChevronUp, Sparkles,
  Wand2, Info, CheckCircle2,
} from "lucide-react";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import DerivedAnthropometricsStrip from "./DerivedAnthropometricsStrip";

const CATEGORY_ACCENT = {
  reproductive: "text-violet-500 bg-violet-500/10 border-violet-500/30",
  anthropometric: "text-sky-500 bg-sky-500/10 border-sky-500/30",
  family_history: "text-fuchsia-500 bg-fuchsia-500/10 border-fuchsia-500/30",
  medical_history: "text-amber-500 bg-amber-500/10 border-amber-500/30",
  benign: "text-teal-500 bg-teal-500/10 border-teal-500/30",
  pregnancy_related_endocrine_disorder:
    "text-pink-500 bg-pink-500/10 border-pink-500/30",
};

function accentFor(cat) {
  return CATEGORY_ACCENT[cat] || "text-slate-500 bg-slate-500/10 border-slate-500/30";
}

export default function ReferenceContextSection() {
  const { API } = useAuth();
  const { isDark } = useTheme();

  const [loading, setLoading] = useState(true);
  const [payload, setPayload] = useState({ groups: [], total: 0 });
  const [saved, setSaved] = useState({});   // { factor_id: level_key }
  const [initial, setInitial] = useState({});
  const [saving, setSaving] = useState(false);
  const [openCats, setOpenCats] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/bhi/risk-factors/reference-context`);
      setPayload(data);
      // Seed local `saved` from user-provided selections only (auto-populated
      // stays as a hint — no save until the user confirms it).
      const initFromUser = {};
      (data.groups || []).forEach((g) => g.factors.forEach((f) => {
        if (f.saved_level) initFromUser[f.id] = f.saved_level;
      }));
      setSaved(initFromUser);
      setInitial(initFromUser);
      const firstCat = (data.groups || [])[0]?.category;
      setOpenCats(firstCat ? { [firstCat]: true } : {});
    } catch {
      toast.error("Грешка при зареждане на референтния контекст.");
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => { load(); }, [load]);

  const setLevel = (fid, key) => {
    setSaved((s) => {
      const next = { ...s };
      if (!key) delete next[fid];
      else next[fid] = key;
      return next;
    });
  };

  const acceptAutoFor = (f) => {
    if (f.auto_populated) setLevel(f.id, f.auto_populated);
  };

  const acceptAllAutoFills = () => {
    setSaved((s) => {
      const next = { ...s };
      (payload.groups || []).forEach((g) => g.factors.forEach((f) => {
        if (f.auto_populated && !next[f.id]) next[f.id] = f.auto_populated;
      }));
      return next;
    });
  };

  const dirty = useMemo(() => {
    const ks = new Set([...Object.keys(saved), ...Object.keys(initial)]);
    for (const k of ks) if (saved[k] !== initial[k]) return true;
    return false;
  }, [saved, initial]);

  const save = async () => {
    setSaving(true);
    const attempted = { ...saved };
    try {
      const { data } = await axios.post(
        `${API}/bhi/risk-factors/reference-context/selection`,
        { selections: attempted },
      );
      setInitial(attempted);
      toast.success(`Запазени ${data.selected_count} елемента от контекста`);
      window.dispatchEvent(new CustomEvent("bhi-reference-context-changed"));
    } catch (err) {
      setSaved({ ...initial });
      const detail = err?.response?.data?.detail || "Грешка при запазване.";
      toast.error(typeof detail === "string" ? detail : "Грешка при запазване.");
    } finally {
      setSaving(false);
    }
  };

  const reset = () => setSaved({ ...initial });

  const filledCount = useMemo(() => {
    let n = 0;
    (payload.groups || []).forEach((g) => g.factors.forEach((f) => {
      const eff = saved[f.id] || f.auto_populated;
      if (eff) n += 1;
    }));
    return n;
  }, [saved, payload]);

  const cardCls = isDark ? "bg-slate-800/60 border-slate-700" : "bg-white border-slate-200";
  const chipCls = isDark ? "bg-slate-700/60 text-slate-100" : "bg-slate-100 text-slate-700";
  const mutedCls = isDark ? "text-slate-400" : "text-slate-500";
  const autoChipCls = "text-sky-500 bg-sky-500/10 border border-sky-500/30";

  const anyAutoUnaccepted = useMemo(() => {
    return (payload.groups || []).some((g) => g.factors.some((f) => f.auto_populated && !saved[f.id]));
  }, [payload, saved]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-8 justify-center" data-testid="ref-ctx-loading">
        <Loader2 className="w-4 h-4 animate-spin" />
        <span className="text-sm">Зареждане на данни от твоя профил…</span>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="reference-context-section">
      {/* iter1001 — Read-only derived anthropometrics tiles */}
      <DerivedAnthropometricsStrip derived={payload.derived_anthropometrics} isDark={isDark} />
      {/* Intro */}
      <div className={`${cardCls} border rounded-xl p-4`} data-testid="ref-ctx-intro">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-violet-500/15 text-violet-500 flex items-center justify-center shrink-0">
            <Info className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-sm md:text-base leading-tight">
              Данни от твоя профил, които участват в изчислението
            </h4>
            <p className={`text-xs md:text-sm mt-1 leading-relaxed ${mutedCls}`}>
              Тези {payload.total} фактора не се променят с времето, но влияят на биологичната ти среда.
              Тези с <span className="text-sky-500 font-medium">синьо</span> ниво са автоматично
              попълнени от профила ти — можеш да ги приемеш или замениш.
            </p>
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${chipCls}`}>
                Активни: <b>{filledCount}</b> / {payload.total}
              </span>
              {anyAutoUnaccepted && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={acceptAllAutoFills}
                  data-testid="ref-ctx-accept-all"
                  className="border-sky-500/50 text-sky-500 hover:bg-sky-500/10"
                >
                  <Wand2 className="w-3.5 h-3.5 mr-1.5" />
                  Приеми всички автоматично попълнени
                </Button>
              )}
              <Button
                size="sm"
                disabled={!dirty || saving}
                onClick={save}
                data-testid="ref-ctx-save"
                className="bg-violet-500 hover:bg-violet-600 text-white disabled:bg-slate-200 disabled:text-slate-600 dark:disabled:bg-slate-700 dark:disabled:text-slate-300"
              >
                {saving
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                  : <Save className="w-3.5 h-3.5 mr-1.5" />}
                Запази данните
              </Button>
              <Button size="sm" variant="ghost" disabled={!dirty || saving} onClick={reset} data-testid="ref-ctx-reset">
                <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Отказ
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Groups */}
      {(payload.groups || []).map((g) => {
        const isOpen = !!openCats[g.category];
        const filledInCat = g.factors.filter((f) => saved[f.id] || f.auto_populated).length;
        return (
          <div
            key={g.category}
            className={`${cardCls} border rounded-xl overflow-hidden`}
            data-testid={`ref-ctx-group-${g.category}`}
          >
            <button
              type="button"
              onClick={() => setOpenCats((s) => ({ ...s, [g.category]: !s[g.category] }))}
              className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-black/[0.03] dark:hover:bg-white/[0.03] transition"
              data-testid={`ref-ctx-group-${g.category}-toggle`}
            >
              <div className={`w-8 h-8 rounded-lg border flex items-center justify-center shrink-0 ${accentFor(g.category)}`}>
                <Info className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm md:text-base">{g.label_bg}</div>
                <div className={`text-xs ${mutedCls}`}>
                  {filledInCat} от {g.factors.length} активни
                </div>
              </div>
              {isOpen
                ? <ChevronUp className="w-4 h-4 shrink-0" />
                : <ChevronDown className="w-4 h-4 shrink-0" />}
            </button>

            {isOpen && (
              <div className="px-3 md:px-4 pb-4 pt-1 grid grid-cols-1 md:grid-cols-2 gap-3">
                {g.factors.map((f) => {
                  const userVal = saved[f.id] || "";
                  const effective = userVal || (f.auto_populated || "");
                  const isAuto = !userVal && !!f.auto_populated;
                  const cardStyle = effective
                    ? isAuto
                      ? "border-sky-500/60 ring-1 ring-sky-500/30"
                      : "border-violet-500/60 ring-1 ring-violet-500/30"
                    : (isDark ? "border-slate-700" : "border-slate-200");
                  return (
                    <div
                      key={f.id}
                      className={`rounded-lg border p-3 flex flex-col gap-2 ${cardStyle} ${
                        isDark ? "bg-slate-900/40" : "bg-slate-50/60"
                      }`}
                      data-testid={`ref-ctx-factor-${f.id}`}
                    >
                      <div className="flex items-start gap-2">
                        <div className={`w-8 h-8 rounded-md flex items-center justify-center shrink-0 ${accentFor(f.category)}`}>
                          <Info className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold leading-tight break-words">
                            {f.name_bg}
                          </div>
                          {f.tooltip_bg && (
                            <div className={`text-[11px] mt-0.5 leading-snug ${mutedCls}`}>
                              {f.tooltip_bg}
                            </div>
                          )}
                          {isAuto && f.auto_source_bg && (
                            <div className={`text-[11px] mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full ${autoChipCls}`}>
                              <Wand2 className="w-3 h-3" />
                              {f.auto_source_bg}
                            </div>
                          )}
                        </div>
                        {isAuto && (
                          <button
                            type="button"
                            onClick={() => acceptAutoFor(f)}
                            className="text-[11px] text-sky-500 hover:underline whitespace-nowrap"
                            data-testid={`ref-ctx-factor-${f.id}-accept`}
                          >
                            Приеми
                          </button>
                        )}
                      </div>

                      <Select
                        value={userVal || (isAuto ? f.auto_populated : "__none__")}
                        onValueChange={(v) => setLevel(f.id, v === "__none__" ? "" : v)}
                      >
                        <SelectTrigger className={`h-8 text-xs ${isAuto ? "text-sky-500" : ""}`}
                                        data-testid={`ref-ctx-factor-${f.id}-select`}>
                          <SelectValue placeholder="Изберете ниво…" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">— Няма избор —</SelectItem>
                          {f.levels.map((lv) => (
                            <SelectItem key={lv.key} value={lv.key}
                                        data-testid={`ref-ctx-factor-${f.id}-level-${lv.key}`}>
                              {lv.label_bg}
                              {isAuto && lv.key === f.auto_populated ? " · (auto)" : ""}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      {userVal && (
                        <div className={`flex items-center gap-1.5 text-[11px] font-medium ${
                          initial[f.id] === userVal ? "text-violet-500" : "text-emerald-500"
                        }`}>
                          <CheckCircle2 className="w-3.5 h-3.5" />
                          {initial[f.id] === userVal ? "Запазено" : "Избрано (незапазено)"}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
